// Minimal ICU-style message formatter. Supports simple placeholders (`{name}`), plural arguments
// (`{count, plural, one {…} other {…}}` including exact selectors such as `=0`) and the `#` token
// inside a plural branch. Plural categories and number formatting come from the `Intl` built-ins,
// so the package stays free of runtime dependencies (ADR 0010).

import { type CatalogLocale, SOURCE_LOCALE } from './namespaces'

export type MessageParam = string | number | boolean | null | undefined

export type MessageParams = Readonly<Record<string, MessageParam>>

const pluralRulesByLocale = new Map<string, Intl.PluralRules>()
const numberFormatByLocale = new Map<string, Intl.NumberFormat>()

/**
 * Renders `message` with `params`. A placeholder without a matching parameter is left in place, so
 * a forgotten argument is visible instead of silently empty. A malformed message or a plural
 * without a numeric argument throws: catalogues are committed source and covered by tests.
 */
export function formatMessage(
  message: string,
  params: MessageParams = {},
  locale: CatalogLocale = SOURCE_LOCALE,
): string {
  if (!message.includes('{')) return message
  return render(message, params, locale, undefined)
}

function render(
  input: string,
  params: MessageParams,
  locale: CatalogLocale,
  pluralValue: number | undefined,
): string {
  let output = ''
  let index = 0

  while (index < input.length) {
    const character = input.charAt(index)

    if (character === '#' && pluralValue !== undefined) {
      output += formatNumber(pluralValue, locale)
      index += 1
      continue
    }

    if (character !== '{') {
      output += character
      index += 1
      continue
    }

    const end = findClosingBrace(input, index)
    output += renderArgument(input.slice(index + 1, end), params, locale)
    index = end + 1
  }

  return output
}

function renderArgument(argument: string, params: MessageParams, locale: CatalogLocale): string {
  const typeSeparator = argument.indexOf(',')

  if (typeSeparator === -1) {
    const name = argument.trim()
    const value = params[name]
    return value === undefined || value === null ? `{${name}}` : String(value)
  }

  const name = argument.slice(0, typeSeparator).trim()
  const remainder = argument.slice(typeSeparator + 1)
  const branchSeparator = remainder.indexOf(',')
  const type = (branchSeparator === -1 ? remainder : remainder.slice(0, branchSeparator)).trim()

  if (type !== 'plural') {
    throw new Error(`Unsupported message argument type '${type}' in '{${argument}}'`)
  }

  const count = Number(params[name])
  if (!Number.isFinite(count)) {
    throw new Error(`Plural argument '${name}' needs a number, received '${String(params[name])}'`)
  }

  const branches = parseBranches(remainder.slice(branchSeparator + 1))
  const branch = selectBranch(branches, count, locale)
  if (branch === undefined) {
    throw new Error(`Plural argument '${name}' has no matching branch for ${count}`)
  }

  return render(branch, params, locale, count)
}

function parseBranches(input: string): ReadonlyMap<string, string> {
  const branches = new Map<string, string>()
  let index = 0

  while (index < input.length) {
    const open = input.indexOf('{', index)
    if (open === -1) break

    const selector = input.slice(index, open).trim()
    if (selector === '') {
      throw new Error(`Plural branch without a selector in '${input}'`)
    }

    const close = findClosingBrace(input, open)
    branches.set(selector, input.slice(open + 1, close))
    index = close + 1
  }

  if (branches.size === 0) {
    throw new Error(`Plural argument without branches in '${input}'`)
  }

  return branches
}

function selectBranch(
  branches: ReadonlyMap<string, string>,
  count: number,
  locale: CatalogLocale,
): string | undefined {
  return (
    branches.get(`=${count}`) ??
    branches.get(pluralCategory(count, locale)) ??
    branches.get('other')
  )
}

function findClosingBrace(input: string, open: number): number {
  let depth = 0

  for (let index = open; index < input.length; index += 1) {
    const character = input.charAt(index)
    if (character === '{') depth += 1
    else if (character === '}') {
      depth -= 1
      if (depth === 0) return index
    }
  }

  throw new Error(`Unbalanced braces in message '${input}'`)
}

function pluralCategory(count: number, locale: CatalogLocale): Intl.LDMLPluralRule {
  let rules = pluralRulesByLocale.get(locale)
  if (rules === undefined) {
    rules = new Intl.PluralRules(locale)
    pluralRulesByLocale.set(locale, rules)
  }
  return rules.select(count)
}

function formatNumber(value: number, locale: CatalogLocale): string {
  let format = numberFormatByLocale.get(locale)
  if (format === undefined) {
    format = new Intl.NumberFormat(locale)
    numberFormatByLocale.set(locale, format)
  }
  return format.format(value)
}
